// In-flight highlight-reel draft.
//
// The user browses the clip library and clicks "+ add" on individual cards.
// We stash the resulting order in localStorage so navigating between /clips
// and /clips/composer doesn't wipe the selection.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface HighlightDraft {
  clipIds: string[];
  toggle: (id: string) => void;
  add: (id: string) => void;
  insertAt: (id: string, index: number) => void;
  remove: (id: string) => void;
  pruneGone: (ids: string[]) => void;
  move: (from: number, to: number) => void;
  reorder: (nextIds: string[]) => void;
  clear: () => void;
}

export const useHighlightDraft = create<HighlightDraft>()(
  persist(
    (set, get) => ({
      clipIds: [],
      toggle: (id: string) => {
        const cur = get().clipIds;
        set({ clipIds: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
      },
      add: (id: string) => {
        const cur = get().clipIds;
        if (!cur.includes(id)) set({ clipIds: [...cur, id] });
      },
      insertAt: (id: string, index: number) => {
        const cur = get().clipIds;
        const from = cur.indexOf(id);
        if (from >= 0) {
          const to = Math.max(0, Math.min(index, cur.length - 1));
          if (from !== to) get().move(from, to);
          return;
        }
        const next = [...cur];
        next.splice(Math.max(0, Math.min(index, next.length)), 0, id);
        set({ clipIds: next });
      },
      remove: (id: string) => set({ clipIds: get().clipIds.filter((x) => x !== id) }),
      pruneGone: (ids: string[]) => {
        if (ids.length === 0) return;
        const drop = new Set(ids);
        const cur = get().clipIds;
        const next = cur.filter((id) => !drop.has(id));
        if (next.length !== cur.length) set({ clipIds: next });
      },
      move: (from, to) => {
        const next = [...get().clipIds];
        if (from < 0 || to < 0 || from >= next.length || to >= next.length) return;
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        set({ clipIds: next });
      },
      reorder: (nextIds) => set({ clipIds: nextIds }),
      clear: () => set({ clipIds: [] }),
    }),
    { name: 'origo-highlight-draft' },
  ),
);
